import { pricing, ModalityKey, WhiteBackgroundTier } from "./pricing";

export type ProductType =
  | "footwear"
  | "apparel"
  | "cosmetics"
  | "food_beverage"
  | "jewelry_accessories"
  | "industrial"
  | "other";

export const PRODUCT_TYPE_LABEL: Record<ProductType, string> = {
  footwear: "Calzado",
  apparel: "Ropa / textiles",
  cosmetics: "Cosméticos / cuidado personal",
  food_beverage: "Alimentos / bebidas",
  jewelry_accessories: "Joyería / accesorios",
  industrial: "Equipo / producto industrial",
  other: "Otro"
};

export const MODALITY_LABEL: Record<ModalityKey, string> = {
  whiteBackground: "Fondo blanco / catálogo",
  flatLay: "Flat lay",
  invisibleMannequin: "Maniquí invisible",
  ambient: "Ambientada / marca",
  model: "Con modelo",
  onLocation: "En instalaciones"
};

export interface ScopeInput {
  productType: ProductType;
  modality: ModalityKey;
  productCount: number;
  photosPerProduct: number;
}

export type QuoteResult =
  | {
      kind: "automatic";
      totalImages: number;
      perImage: number;
      amount: number;
      requiresProductionValidation: boolean;
    }
  | {
      kind: "production";
      totalImages: number;
    };

/** total_imagenes = productos × fotos_por_producto — always live, never hardcoded. */
export function computeTotalImages(productCount: number, photosPerProduct: number): number {
  return Math.max(0, Math.trunc(productCount)) * Math.max(0, Math.trunc(photosPerProduct));
}

/**
 * White-background tiered pricing with a continuity floor: the subtotal can
 * never go DOWN when totalImages goes UP, even across a tier boundary.
 */
export function priceWhiteBackground(totalImages: number): number {
  if (totalImages <= 0) return 0;
  const tiers = pricing.whiteBackground.tiers as WhiteBackgroundTier[];

  const tierFor = (n: number) =>
    tiers.find((t) => n >= t.min && (t.max === null || n <= t.max)) ?? tiers[tiers.length - 1];

  const rawAt = (n: number) => n * tierFor(n).perImage;

  // Continuity floor: the amount for `totalImages` can never be less than the
  // amount at the last image of the tier below it (monotonic non-decreasing).
  let floor = 0;
  for (const tier of tiers) {
    if (totalImages >= tier.min) {
      const tierEnd = tier.max !== null ? Math.min(tier.max, totalImages) : totalImages;
      floor = Math.max(floor, tierEnd * tier.perImage);
    }
  }

  return Math.max(rawAt(totalImages), floor);
}

/**
 * Computes the estimate for a given scope. Returns a "production" result
 * (no automatic price) for modalities that require the production team's
 * review, per the approved flow.
 */
export function computeQuote(input: ScopeInput): QuoteResult {
  const totalImages = computeTotalImages(input.productCount, input.photosPerProduct);
  const modality = pricing[input.modality];

  if (!modality.automaticPricing) {
    return { kind: "production", totalImages };
  }

  if (input.modality === "whiteBackground") {
    const amount = priceWhiteBackground(totalImages);
    const effectivePerImage = totalImages > 0 ? Math.round(amount / totalImages) : 0;
    return {
      kind: "automatic",
      totalImages,
      perImage: effectivePerImage,
      amount,
      requiresProductionValidation: false
    };
  }

  const perImage = (modality as { fromPerImage: number }).fromPerImage;
  const requiresProductionValidation =
    "requiresProductionValidation" in modality
      ? Boolean((modality as { requiresProductionValidation?: boolean }).requiresProductionValidation)
      : false;

  return {
    kind: "automatic",
    totalImages,
    perImage,
    amount: totalImages * perImage,
    requiresProductionValidation
  };
}

export function formatMXN(amount: number): string {
  return new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    maximumFractionDigits: 0
  }).format(amount);
}
